import {
    Close as CloseIcon,
    KeyboardArrowDown,
    Menu as MenuIcon,
    Notifications,
    Search as SearchIcon
} from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    Chip,
    Drawer,
    Fade,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Menu,
    MenuItem as MUIMenuItem,
    Toolbar,
    useTheme
} from '@mui/material';
import React, { useState } from 'react';
import { UserAvatar } from '../../../../Components';
import ApplicationLogo from '../../../../Components/ApplicationLogo';
import LanguagesMenu from '../../../../Components/LanguagesMenu';
import { getOrDefault, isRTL } from '../../../../Helpers/Utils';
import { MenuItem, ModernNavbarProps } from './types';

const ModernNavbar: React.FC<ModernNavbarProps> = ({ spec, type, index }) => {
    const theme = useTheme();

    // Extract spec properties with defaults
    const backgroundColor = getOrDefault(spec.backgroundColor, 'rgba(255, 255, 255, 0.95)');
    const textColor = getOrDefault(spec.textColor, theme.palette.text.primary);
    const height = getOrDefault(spec.height, '70px');
    const transparent = getOrDefault(spec.transparent, false);
    const blur = getOrDefault(spec.blur, true);
    const shadow = getOrDefault(spec.shadow, true);
    const sticky = getOrDefault(spec.sticky, true);
    const menuItems = getOrDefault(spec.menuItems, []);
    const showLanguagesMenu = getOrDefault(spec.showLanguagesMenu, false);
    const showSearch = getOrDefault(spec.showSearch, false);
    const showNotifications = getOrDefault(spec.showNotifications, false);
    const showProfile = getOrDefault(spec.showProfile, false);
    const brandColor = getOrDefault(spec.brandColor, theme.palette.primary.main);

    // State management
    const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

    // Create dynamic styles
    const navbarStyles = {
        background: transparent
            ? 'transparent'
            : backgroundColor,
        backdropFilter: blur ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: blur ? 'blur(10px)' : 'none',
        boxShadow: shadow
            ? '0 4px 20px rgba(0, 0, 0, 0.1)'
            : 'none',
        position: sticky ? 'sticky' : 'static',
        top: sticky ? 0 : 'auto',
        zIndex: 1100,
        height,
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: shadow
                ? '0 8px 32px rgba(0, 0, 0, 0.15)'
                : 'none'
        }
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchor(null);
    };

    // Desktop menu items
    const DesktopMenuItems: React.FC = () => (
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
            {menuItems.map((item: MenuItem, idx: number) => (
                <Button
                    key={idx}
                    href={item.href}
                    sx={{
                        color: textColor,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '1.15rem',
                        px: 2,
                        py: 1,
                        borderRadius: 0,
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            color: brandColor,
                            backgroundColor: `${brandColor}10`,
                            transform: 'translateY(-1px)'
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            width: 0,
                            height: '2px',
                            background: brandColor,
                            transition: 'all 0.3s ease',
                            transform: 'translateX(-50%)'
                        },
                        '&:hover::after': {
                            width: '80%'
                        }
                    }}
                    endIcon={item.hasSubmenu ? <KeyboardArrowDown /> : null}
                >
                    {item.label}
                    {item.badge && (
                        <Chip
                            label={item.badge}
                            size="small"
                            sx={{
                                ml: 1,
                                height: 20,
                                fontSize: '0.7rem',
                                background: brandColor,
                                color: 'white'
                            }}
                        />
                    )}
                </Button>
            ))}
        </Box>
    );

    // Action buttons
    const ActionButtons: React.FC = () => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showSearch && (
                <IconButton
                    sx={{
                        color: textColor,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            color: brandColor,
                            backgroundColor: `${brandColor}10`,
                            transform: 'scale(1.1)'
                        }
                    }}
                >
                    <SearchIcon />
                </IconButton>
            )}

            {showLanguagesMenu && (
                <LanguagesMenu />
            )}

            {showNotifications && (
                <IconButton
                    sx={{
                        color: textColor,
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            color: brandColor,
                            backgroundColor: `${brandColor}10`,
                            transform: 'scale(1.1)'
                        }
                    }}
                >
                    <Notifications />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 8,
                            height: 8,
                            background: 'red',
                            borderRadius: '50%',
                            animation: 'pulse 2s infinite'
                        }}
                    />
                </IconButton>
            )}

            {showProfile && (
                <UserAvatar 
                    size="medium"
                    showTooltip={false}
                    sx={{}}
                />
            )}
        </Box>
    );

    // Mobile drawer
    const MobileDrawer: React.FC = () => (
        <Drawer
            anchor={isRTL() ? 'left' : 'right'}
            open={mobileMenuOpen}
            onClose={handleMobileMenuToggle}
            PaperProps={{
                sx: {
                    width: 280,
                    background: backgroundColor,
                    backdropFilter: 'blur(20px)'
                }
            }}
            dir={isRTL() ? 'rtl' : 'ltr'}
        >
            <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
            }}>
                <ApplicationLogo enableHoveringEffect={false} animate={false} overrideDefaultLogo={false} />
                <IconButton
                    onClick={handleMobileMenuToggle}
                    sx={{
                        height: 40,
                        width: 40,
                        color: textColor
                    }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <List>
                {menuItems.map((item: MenuItem, idx: number) => (
                    <ListItem
                        key={idx}
                        component="a"
                        href={item.href}
                        sx={{
                            mx: 1,
                            mb: 1,
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: `${brandColor}20`,
                                transform: 'translateX(10px)'
                            }
                        }}
                    >
                        <ListItemText
                            primary={item.label}
                            sx={{ color: textColor }}
                        />
                        {item.badge && (
                            <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    background: brandColor,
                                    color: 'white'
                                }}
                            />
                        )}
                    </ListItem>
                ))}
            </List>
        </Drawer>
    );

    return (
        <Box id={spec.id} key={`${type}-${index}-box`}>
            <AppBar
                position={sticky ? 'sticky' : 'static'}
                elevation={0}
                sx={navbarStyles}
            >
                <Toolbar
                    sx={{
                        minHeight: height,
                        height: height,
                        px: { xs: 2, md: 4 },
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <ApplicationLogo enableHoveringEffect={false} animate={false} overrideDefaultLogo={false} />

                    <DesktopMenuItems />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ActionButtons />

                        {/* Mobile menu button */}
                        <IconButton
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                color: textColor,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    color: brandColor,
                                    backgroundColor: `${brandColor}10`,
                                    transform: 'rotate(90deg)'
                                }
                            }}
                            onClick={handleMobileMenuToggle}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <MobileDrawer />

            {/* Profile Menu */}
            <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: 0,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        backdropFilter: 'blur(10px)',
                        background: 'rgba(255, 255, 255, 0.95)'
                    }
                }}
            >
                <MUIMenuItem onClick={handleProfileMenuClose}>الملف الشخصي</MUIMenuItem>
                <MUIMenuItem onClick={handleProfileMenuClose}>الإعدادات</MUIMenuItem>
                <MUIMenuItem onClick={handleProfileMenuClose}>تسجيل الخروج</MUIMenuItem>
            </Menu>

            {/* Add pulse animation */}
            <style>
                {`
                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.5; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
        </Box>
    );
};

export default ModernNavbar;
